import React, { useState, useEffect } from "react";
import { Col } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import {
  formatBigNum,
  chartUserBanner,
  chartWatermarkCenter,
  chartBgc,
  capitalize,
  chartDataSorter,
} from "../utils";

const HarmonicDiscountChart = ({
  dataFetched,
  isLoading,
  chartData,
  startDate,
  endDate,
  graphRef,
  tickerName,
  domainName,
  categoryName,
  setSelectedDate,
  yoyToggle,
  legendCache,
  setLegendCache,
  showLeftAxis,
}) => {
  const [displayedDates, setDisplayedDates] = useState([]);
  const [effectiveDiscount, setEffectiveDiscount] = useState([]);
  const [effectiveDiscountYoY, setEffectiveDiscountYoY] = useState([]);
  const [storewideTotalVolume, setStoreWideTotalVolume] = useState([]);
  const [storewideTotalVolumeYoY, setStoreWideTotalVolumeYoY] = useState([]);
  const [significantTotalVolume, setSignificantTotalVolume] = useState([]);
  const [significantTotalVolumeYoY, setSignificantTotalVolumeYoY] = useState([]);
  const [steepTotalVolume, setSteepTotalVolume] = useState([]);
  const [steepTotalVolumeYoY, setSteepTotalVolumeYoY] = useState([]);
  const [maxDiscount, setMaxDiscount] = useState([]);
  const [maxDiscountYoY, setMaxDiscountYoY] = useState([]);
  const [adjustedDiscountVol, setAdjustedDiscountVol] = useState([]);
  const [adjustedTotalVol, setAdjustedTotalVol] = useState([]);
  const [adjustedTotalVolYoY, setAdjustedTotalVolYoY] = useState([]);
  const [adjustedBadVol, setAdjustedBadVol] = useState([]);
  const [adjustedBadVolYoY, setAdjustedBadVolYoY] = useState([]);
  const [adjustedSpamPercent, setAdjustSpamPercent] = useState([]);
  const [adjustedDeletionPercent, setAdjustedDeletionPercent] = useState([]);
  const [adjustedReadPercent, setAdjustedReadPercent] = useState([]);



  // whether display legend & its corresponding data (true: hide, false: show)
  useEffect(() => {
    if (setLegendCache) {
      setLegendCache({
        "Effective Discount": true,
        "Max Discount": true,
        "Total Volume": true,
        "Discount Volume": true,
        "# of Significant Discounts": false,
        "# of Steep Discounts": false,
        "# of Storewide Discounts": false,
        "# of Desperation Discounts": false,
        "Spam%": true,
        "Deletion%": true
      });
    }
  }, [setLegendCache]);

  const loadDisplayData = () => {
    let tempEffectiveData = [];
    let tempEffectiveYoYData = [];
    let tempMaxdiscountYoYData = [];
    let tempStorewideData = [];
    let tempStorewideYoYData = [];
    let tempSignificantData = [];
    let tempSignificantYoYData = [];
    let tempSteepData = [];
    let tempSteepYoYData = [];
    let tempMaxdiscountData = [];
    let tempDateData = [];
    let tempAdjustedDiscVolData = [];
    let tempAdjustedTotalVolData = [];
    let tempAdjustedTotalVolYoYData = [];
    let tempAdjustedBadVolData = [];
    let tempAdjustedBadVolYoYData = [];

    let tempAdjustedSpamPercent = [];
    let tempAdjustedDeletionPercent = [];
    let tempAdjustedReadPercent = [];

    chartData.sort(chartDataSorter);

    for (let i = 0; i < chartData.length; i++) {
      if (
        new Date(chartData[i].start_date) >= new Date(startDate) &&
        new Date(chartData[i].end_date) <= new Date(endDate)
      ) {
        tempDateData.push(chartData[i].end_date);
        tempEffectiveData.push(chartData[i].effective_discount);
        tempEffectiveYoYData.push(chartData[i].effective_discount_yoy);
        tempMaxdiscountYoYData.push(chartData[i].max_discount_yoy);
        tempStorewideData.push(
          chartData[i].adjusted_harmonic_is_storewide_total_volume
        );
        tempStorewideYoYData.push(
          chartData[i].adjusted_harmonic_is_storewide_total_volume_yoy
        );
        tempSignificantData.push(
          chartData[i].adjusted_harmonic_is_significant_total_volume
        );
        tempSignificantYoYData.push(
          chartData[i].adjusted_harmonic_is_significant_total_volume_yoy
        );
        tempSteepData.push(
          chartData[i].adjusted_harmonic_is_steep_total_volume
        );
        tempSteepYoYData.push(
          chartData[i].adjusted_harmonic_is_steep_total_volume_yoy
        );
        tempMaxdiscountData.push(chartData[i].max_discount);
        tempAdjustedDiscVolData.push(chartData[i].adjusted_discount_vol);
        tempAdjustedTotalVolData.push(chartData[i].adjusted_total_volume);
        tempAdjustedTotalVolYoYData.push(chartData[i].adjusted_total_volume_yoy);
        tempAdjustedBadVolData.push(
          chartData[i].adjusted_badwords_total_volume
        );
        tempAdjustedBadVolYoYData.push(chartData[i].adjusted_badwords_total_volume_yoy);
        tempAdjustedSpamPercent.push(
          chartData[i].adjusted_spam_volume / (chartData[i].adjusted_spam_volume + chartData[i].adjusted_inbox_volume) * 100
        )
        tempAdjustedDeletionPercent.push(
          chartData[i].adjusted_delete_volume / chartData[i].adjusted_total_volume * 100
        )
        tempAdjustedReadPercent.push(
          chartData[i].adjusted_read_volume / chartData[i].adjusted_total_volume * 100
        )
      }
    }

    setDisplayedDates(tempDateData);
    setEffectiveDiscount(tempEffectiveData);
    setEffectiveDiscountYoY(tempEffectiveYoYData);
    setMaxDiscountYoY(tempMaxdiscountYoYData);
    setStoreWideTotalVolume(tempStorewideData);
    setStoreWideTotalVolumeYoY(tempStorewideYoYData);
    setSignificantTotalVolume(tempSignificantData);
    setSignificantTotalVolumeYoY(tempSignificantYoYData);
    setMaxDiscount(tempMaxdiscountData);
    setSteepTotalVolume(tempSteepData);
    setSteepTotalVolumeYoY(tempSteepYoYData);
    setAdjustedDiscountVol(tempAdjustedDiscVolData);
    setAdjustedTotalVol(tempAdjustedTotalVolData);
    setAdjustedTotalVolYoY(tempAdjustedTotalVolYoYData);
    setAdjustedBadVol(tempAdjustedBadVolData);
    setAdjustedBadVolYoY(tempAdjustedBadVolYoYData);
    setAdjustSpamPercent(tempAdjustedSpamPercent);
    setAdjustedDeletionPercent(tempAdjustedDeletionPercent);
    setAdjustedReadPercent(tempAdjustedReadPercent);
  };

  useEffect(() => {
    loadDisplayData();
  }, [dataFetched, chartData, startDate, endDate]);

  if (!dataFetched || !legendCache) {
    return <div></div>;
  } else if (isLoading) {
    return <h4 style={{ textAlign: "center" }}>Loading Chart...</h4>;
  }

  return (
    <Col sm={12} style={{ height: 600 }}>
      <React.Fragment>
        <Line
          ref={graphRef}
          className="history-container"
          data={{
            labels: displayedDates,
            datasets: [
              {
                data: yoyToggle ? effectiveDiscountYoY : effectiveDiscount,
                type: "line",
                label: "Effective Discount",
                borderColor: "#36a2eb",
                yAxisID: "B",
                fill: false,
                borderWidth: 3,
                borderDash: yoyToggle ? [5, 5] : [],
                hidden: legendCache["Effective Discount"],
              },
              {
                data: yoyToggle ? maxDiscountYoY : maxDiscount,
                type: "line",
                label: "Max Discount",
                borderColor: "#808080",
                yAxisID: "B",
                fill: false,
                borderWidth: 2,
                borderDash: yoyToggle ? [5, 5] : [],
                hidden: legendCache["Max Discount"],
              },
              {
                data: yoyToggle ? adjustedTotalVolYoY : adjustedTotalVol,
                type: yoyToggle ? "line" : "bar",
                label: "Total Volume",
                backgroundColor: yoyToggle ? "transparent" : "#a51a56a4",
                borderColor: yoyToggle ? "#a51a56" : "#a51a56a4",
                yAxisID: yoyToggle ? "B" : "A",
                fill: false,
                borderWidth: yoyToggle ? 3 : 1,
                borderDash: yoyToggle ? [5, 5] : [],
                hidden: legendCache["Total Volume"],
              },
              {
                data: yoyToggle
                  ? significantTotalVolumeYoY
                  : significantTotalVolume,
                type: yoyToggle ? "line" : "bar",
                label: "# of Significant Discounts",
                backgroundColor: yoyToggle ? "transparent" : "#ff9f40",
                borderColor: yoyToggle ? "#ff9f40" : "#ff9f40",
                yAxisID: yoyToggle ? "B" : "A",
                fill: false,
                borderWidth: yoyToggle ? 3 : 1,
                borderDash: yoyToggle ? [5, 5] : [],
                hidden: legendCache["# of Significant Discounts"],
              },
              {
                data: yoyToggle ? steepTotalVolumeYoY : steepTotalVolume,
                type: yoyToggle ? "line" : "bar",
                label: "# of Steep Discounts",
                backgroundColor: yoyToggle ? "transparent" : "#9966ff",
                borderColor: yoyToggle ? "#9966ff" : "#9966ff",
                yAxisID: yoyToggle ? "B" : "A",
                fill: false,
                borderWidth: yoyToggle ? 3 : 1,
                borderDash: yoyToggle ? [5, 5] : [],
                hidden: legendCache["# of Steep Discounts"],
              },
              {
                data: yoyToggle ? storewideTotalVolumeYoY : storewideTotalVolume,
                type: yoyToggle ? "line" : "bar",
                label: "# of Storewide Discounts",
                backgroundColor: yoyToggle ? "transparent" : "#c9cbcf",
                borderColor: yoyToggle ? "#c9cbcf" : "#c9cbcf",
                yAxisID: yoyToggle ? "B" : "A",
                fill: false,
                borderWidth: yoyToggle ? 3 : 1,
                borderDash: yoyToggle ? [5, 5] : [],
                hidden: legendCache["# of Storewide Discounts"],
              },
              {
                data: yoyToggle ? adjustedBadVolYoY : adjustedBadVol,
                type: yoyToggle ? "line" : "bar",
                label: "# of Desperation Discounts",
                backgroundColor: yoyToggle ? "transparent" : "#fa4d56",
                borderColor: yoyToggle ? "#fa4d56" : "#fa4d56",
                yAxisID: yoyToggle ? "B" : "A",
                fill: false,
                borderWidth: yoyToggle ? 3 : 1,
                borderDash: yoyToggle ? [5, 5] : [],
                hidden: legendCache["# of Desperation Discounts"],
              },
            ],
          }}
          onElementsClick={(elem) => {
            if (elem[0]) {
              setSelectedDate(
                graphRef.current.props.data.labels[elem[0]._index]
              );
            }
          }}
          options={{
            layout: {
              padding: {
                bottom: 40,
              },
            },
            responsive: true,
            maintainAspectRatio: false,
            title: {
              display: true,
              text: `RandomWalk ${domainName} (${tickerName}) Promotional Ensemble`,
              fontSize: 15,
            },
            scales: {
              xAxes: [
                {
                  offset: true,
                  gridLines: {
                    display: false,
                  },
                  type: "time",
                  time: {
                    unit: "week",
                  },
                },
              ],
              yAxes: [
                {
                  id: "A",
                  type: "linear",
                  position: "right",
                  display: !yoyToggle,
                  ticks: {
                    callback: (value) => formatBigNum(value),
                  },
                  gridLines: {
                    borderDash: [8, 4],
                  },
                },
                {
                  id: "B",
                  type: "linear",
                  position: "left",
                  display: showLeftAxis,
                  ticks: {
                    callback: function (value) {
                      return value + "%";
                    },
                  },
                  gridLines: {
                    borderDash: [8, 4],
                  },
                  stacked: false,
                },
              ],
            },
            legend: {
              onClick: (e, legendItem) => {
                const index = legendItem.datasetIndex;
                const ci = graphRef.current.chartInstance;
                const meta = ci.getDatasetMeta(index);

                const isHidden = legendCache[legendItem.text];
                meta.hidden =
                  meta.hidden === null ? !ci.data.datasets[index].hidden : null;
                setLegendCache({
                  ...legendCache,
                  [legendItem.text]: !isHidden,
                });
                ci.update();
              },
            },
          }}
          plugins={[chartBgc, chartWatermarkCenter, chartUserBanner]}
        />

      </React.Fragment>

    </Col>
  );
};

export default React.memo(HarmonicDiscountChart);
